import { assertPageActive, createPaymentPageScope, PaymentPageOperationEndedError } from "./paymentPageOperation.scope";
import { AdyenCheckout, Dropin } from "@adyen/adyen-web/auto";
import {
    ADYEN_RESULT_CODE,
    PAGE_STATE_POLL_INTERVAL_MILLISECONDS,
    PAYMENT_LINK_STATE,
} from "./paymentPage.constants";
import {
    fetchPaymentLinkPage,
    openCheckoutSession,
    sendBankCheckStart,
    sendPaymentDetails,
    sendPaymentResult,
    startPaymentAttempt,
    API_ORIGIN_PARAMETER,
} from "./paymentPage.api";
import { readStoredAttempt, writeStoredAttempt } from "./paymentPageAttempt.store";
import {
    buildCheckoutConfiguration,
    buildDropinConfiguration,
    buildOrderSummary,
    readOrderSummaryAfterPayment,
} from "./paymentPageCheckout.configuration";
import {
    PAGE_VIEW,
    resolveViewForPaidLink,
    resolveViewForDeclinedLink,
    resolveViewForLinkState,
    resolveViewForLoadError,
    resolveViewForPaymentError,
    resolveViewForPaymentResult,
} from "./paymentPage.views";

const TEXT_TYPE = "string";

const OBJECT_TYPE = "object";

export function disposeDropin(pageContext) {
    if (pageContext.dropinInstance.current !== null) {
        pageContext.dropinInstance.current.unmount();
        pageContext.dropinInstance.current = null;
    }
}

function writeBackgroundFailureToConsole(error) {
    if (error instanceof PaymentPageOperationEndedError) return;
    console.error(error);
}

function hasPaymentResult(paymentResult) {
    return paymentResult !== null && typeof paymentResult === OBJECT_TYPE;
}

function hasSessionResult(paymentResult) {
    return typeof paymentResult.sessionResult === TEXT_TYPE;
}

async function readPage(pageContext) {
    assertPageActive(pageContext);
    const page = await fetchPaymentLinkPage(pageContext.paymentLinkId);
    assertPageActive(pageContext);
    return page;
}

async function openDropin(pageContext, paymentLinkPage, checkoutSession) {
    assertPageActive(pageContext);
    const checkout = await AdyenCheckout(
        buildCheckoutConfiguration(paymentLinkPage, checkoutSession, pageContext.dropinCallbacks)
    );
    assertPageActive(pageContext);
    const dropin = new Dropin(checkout, buildDropinConfiguration(paymentLinkPage));
    dropin.mount(pageContext.dropinContainer.current);
    pageContext.dropinInstance.current = dropin;
}

export async function openPaymentForm(pageContext) {
    try {
        const paymentLinkPage = await readPage(pageContext);
        if (paymentLinkPage.state === PAYMENT_LINK_STATE.PAID) {
            pageContext.updateView(resolveViewForPaidLink(true, paymentLinkPage.isCardSaving === true));
            return;
        }
        if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN) {
            pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
            return;
        }
        if (paymentLinkPage.isPaymentInProgress) {
            pageContext.updateView(PAGE_VIEW.PAYMENT_IN_PROGRESS);
            return;
        }
        pageContext.updateOrderSummary(buildOrderSummary(paymentLinkPage));
        const checkoutSession = await openCheckoutSession(pageContext.paymentLinkId);
        await openDropin(pageContext, paymentLinkPage, checkoutSession);
        pageContext.updateView(PAGE_VIEW.PAYMENT_FORM);
    } catch (error) {
        pageContext.updateView(resolveViewForLoadError(error));
    }
}

export async function openPaymentFormAgain(pageContext) {
    disposeDropin(pageContext);
    pageContext.updateView(PAGE_VIEW.LOADING);
    await openPaymentForm(pageContext);
}

export function runPaymentPageReload() {
    window.location.reload();
}

export async function startGatedPayment(pageContext, paymentData, component, actions) {
    try {
        assertPageActive(pageContext);
        const startedAttempt = await startPaymentAttempt(pageContext.paymentLinkId, component.core.session, paymentData.billingAddress);
        assertPageActive(pageContext);
        writeStoredAttempt(pageContext.paymentLinkId, startedAttempt.attemptId);
        actions.resolve({ ...paymentData, sessionData: startedAttempt.sessionData });
    } catch (error) {
        actions.reject();
        pageContext.updateView(resolveViewForPaymentError(error));
    }
}

export async function sendBankCheckStartForAttempt(pageContext) {
    if (!pageContext.isActive()) return;
    const storedAttempt = readStoredAttempt(pageContext.paymentLinkId);
    if (storedAttempt.isStored === false) {
        pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
        return;
    }
    try {
        await sendBankCheckStart(pageContext.paymentLinkId, storedAttempt.attemptId);
    } catch (error) {
        writeBackgroundFailureToConsole(error);
        pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
    }
}

export async function sendBankCheckDetails(pageContext, detailsState, actions) {
    try {
        assertPageActive(pageContext);
        const storedAttempt = requireStoredAttempt(pageContext);
        const detailsResult = await sendPaymentDetails(
            pageContext.paymentLinkId,
            storedAttempt.attemptId,
            detailsState.data
        );
        assertPageActive(pageContext);
        actions.resolve(detailsResult);
    } catch (error) {
        actions.reject();
        pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
    }
}

function requireStoredAttempt(pageContext) {
    const storedAttempt = readStoredAttempt(pageContext.paymentLinkId);
    if (!storedAttempt.isStored) throw new Error("Payment attempt identity is unavailable. Check this payment before continuing.");
    return storedAttempt;
}

async function sendResultForAttempt(pageContext, paymentResult) {
    assertPageActive(pageContext);
    const storedAttempt = requireStoredAttempt(pageContext);
    if (!hasSessionResult(paymentResult)) throw new Error("Processor result is missing its confirmation token.");
    await sendPaymentResult(pageContext.paymentLinkId, storedAttempt.attemptId, paymentResult.sessionResult);
}

async function updateViewForDeclinedLink(pageContext) {
    try {
        const paymentLinkPage = await readPage(pageContext);
        if (paymentLinkPage.state === PAYMENT_LINK_STATE.PAID) {
            pageContext.updateView(resolveViewForPaidLink(false, paymentLinkPage.isCardSaving === true));
        } else if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN && paymentLinkPage.state !== PAYMENT_LINK_STATE.CLOSED) {
            pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
        } else {
            pageContext.updateView(paymentLinkPage.isPaymentInProgress ? PAGE_VIEW.CHECKING_PAYMENT : resolveViewForDeclinedLink(paymentLinkPage.state));
        }
    } catch (error) {
        writeBackgroundFailureToConsole(error);
        pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
    }
}

async function updateViewForPaymentResult(pageContext, resultCode) {
    pageContext.updateView(PAGE_VIEW.CHECKING_PAYMENT);
    if (resultCode === ADYEN_RESULT_CODE.REFUSED) {
        await updateViewForDeclinedLink(pageContext);
        return;
    }
    pageContext.updateView(resolveViewForPaymentResult(resultCode));
}

export async function processPaymentResult(pageContext, paymentResult) {
    if (hasPaymentResult(paymentResult) === false) {
        pageContext.updateView(PAGE_VIEW.CHECKING_PAYMENT);
        return;
    }
    try {
        await sendResultForAttempt(pageContext, paymentResult);
        assertPageActive(pageContext);
        await updateViewForPaymentResult(pageContext, paymentResult.resultCode);
    } catch (error) {
        pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
    }
}

export function handleDropinError(pageContext) {
    pageContext.updateView(PAGE_VIEW.CHECKING_PAYMENT);
}

async function processRedirectPayment(pageContext, paymentLinkPage) {
    if (paymentLinkPage.state === PAYMENT_LINK_STATE.PAID) {
        pageContext.updateView(resolveViewForPaidLink(false, paymentLinkPage.isCardSaving === true));
        return;
    }
    if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN) {
        pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
        return;
    }
    // Adyen completes the payment the shopper made only once the redirect's details are sent, so they
    // are sent before anything else is done with the page; nothing the page shows can stop them.
    const storedAttempt = requireStoredAttempt(pageContext);
    const detailsResult = await sendPaymentDetails(pageContext.paymentLinkId, storedAttempt.attemptId, {
        details: { redirectResult: pageContext.redirectResult.value },
    });
    pageContext.updateOrderSummary(readOrderSummaryAfterPayment(paymentLinkPage));
    await updateViewForPaymentResult(pageContext, detailsResult.resultCode);
}

// Drops the redirect result from the address but keeps the API origin the link came with, so a
// reload after the redirect still talks to the Jellyfish that issued the link.
function removeRedirectResultFromAddress() {
    const kept = new URLSearchParams();
    const current = new URLSearchParams(window.location.search);
    const api = current.get(API_ORIGIN_PARAMETER);
    if (api !== null) kept.set(API_ORIGIN_PARAMETER, api);
    const search = kept.toString();
    window.history.replaceState(window.history.state, document.title, window.location.pathname + (search.length > 0 ? "?" + search : ""));
}

export async function loadPaymentPage(pageContext) {
    if (pageContext.redirectResult.isPresent === false) {
        await openPaymentForm(pageContext);
        return;
    }
    removeRedirectResultFromAddress();
    try {
        await processRedirectPayment(pageContext, await readPage(pageContext));
    } catch (error) {
        pageContext.updateView(resolveViewForPaymentError(error));
    }
}

async function handleWaitingEnded(pageContext, pageView) {
    if (pageView === PAGE_VIEW.CHECKING_PAYMENT) {
        pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
        return;
    }
    await openPaymentFormAgain(pageContext);
}

async function applyPolledPageState(pageContext, pageView, paymentLinkPage) {
    if (paymentLinkPage.state === PAYMENT_LINK_STATE.PAID) {
        pageContext.updateView(resolveViewForPaidLink(false, paymentLinkPage.isCardSaving === true));
        return;
    }
    if (paymentLinkPage.state !== PAYMENT_LINK_STATE.OPEN) {
        pageContext.updateView(resolveViewForLinkState(paymentLinkPage.state));
        return;
    }
    if (paymentLinkPage.isPaymentInProgress) {
        return;
    }
    await handleWaitingEnded(pageContext, pageView);
}

async function updatePolledPageState(pageContext, pageView, pollingContext) {
    try {
        const page = await readPage(pollingContext);
        assertPageActive(pollingContext);
        // Once readiness starts a form, that work belongs to the page, not to the poll being stopped.
        await applyPolledPageState(pageContext, pageView, page);
    } catch (error) {
        writeBackgroundFailureToConsole(error);
        pollingContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
    }
}

export function startPageStatePolling(pageContext, pageView) {
    if (pageView.isPollingPageState === false) return undefined;
    const pollingContext = createPaymentPageScope(pageContext);
    let stopped = false;
    let polls = 0;
    let timer;
    async function poll() {
        if (stopped) return;
        if (++polls > 100) {
            pageContext.updateView(PAGE_VIEW.CONFIRMATION_UNAVAILABLE);
            return;
        }
        await updatePolledPageState(pageContext, pageView, pollingContext);
        if (!stopped) timer = window.setTimeout(poll, PAGE_STATE_POLL_INTERVAL_MILLISECONDS);
    }
    timer = window.setTimeout(poll, PAGE_STATE_POLL_INTERVAL_MILLISECONDS);
    return () => { stopped = true; pollingContext.dispose(); window.clearTimeout(timer); };
}
